/**
 * DEAD SIGNAL — Navigation Menu
 *
 * Persistent "NAVIGATION" button that opens a popup of state-gated
 * destinations for the current scene. The button label never changes;
 * the popup can be closed without navigating.
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useGameStore } from '../state/gameState.js';
import { getNavigationOptions } from '../systems/navigationSystem.js';
import { AudioSystem } from '../systems/AudioSystem.js';

export default function NavigationMenu({ onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState(null); // viewport rect of the button at open time
  const menuRef = useRef(null);

  const sceneId = useGameStore((s) => s.sceneId);
  const carMode = useGameStore((s) => s.carMode);
  const exhaustion = useGameStore((s) => s.exhaustion);
  const demoStage = useGameStore((s) => s.demoStage);

  const options = useMemo(() => {
    return getNavigationOptions(sceneId, useGameStore.getState());
  }, [sceneId, carMode, exhaustion]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [sceneId]);

  const handleToggle = useCallback(() => {
    try { AudioSystem.start(); AudioSystem.playNavigation(); } catch (_) {}
    const r = menuRef.current ? menuRef.current.getBoundingClientRect() : null;
    setAnchor(r ? { left: Math.round(r.right + 6), top: Math.round(r.top) } : null);
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback((option) => {
    try { AudioSystem.start(); AudioSystem.playNavigation(); } catch (_) {}
    setIsOpen(false);
    onNavigate(option.targetScene);
  }, [onNavigate]);

  if (demoStage !== 'playing') return null;
  if (options.length === 0) return null;

  return (
    <div className="nav-menu" ref={menuRef}>
      <button
        type="button"
        className="nav-menu-btn"
        onClick={handleToggle}
      >
        <span className="nav-menu-caret">▸</span> NAVIGATION
      </button>

      {isOpen && (
        // Blocking top layer: a full-viewport backdrop covers the scene +
        // hotspots so a click there closes the menu and never falls through to a
        // hotspot. The popup is a CHILD of the backdrop, so it always stacks
        // above it regardless of ancestor stacking contexts.
        <div
          className="nav-menu-backdrop"
          onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(2, 5, 3, 0.45)' }}
        >
          <div
            className="nav-menu-popup"
            onClick={(e) => e.stopPropagation()}
            style={anchor
              ? { position: 'absolute', left: anchor.left, top: anchor.top, bottom: 'auto', zIndex: 1 }
              : { position: 'absolute', left: 8, bottom: 48, zIndex: 1 }}
          >
            {options.map((opt) => (
              <button
                key={opt.targetScene}
                type="button"
                className="nav-menu-option"
                onClick={() => handleSelect(opt)}
              >
                <span className="nav-menu-option-caret">▸</span> {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
